import { Injectable, Injector, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IdleDialogComponent } from 'app/main/Idle/idle-dialog.component';
import { ALNIdentityAuthService } from '../../core/auth/aln-identity-auth.service';
import { environment } from 'environments/environment';
import { CrossTabSessionService } from '../../core/services/cross-tab-session.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  readonly MAX_IDLE_TIME = parseInt(environment.idleTimeOut);
  readonly Message_TimeOut = parseInt(environment.messageTimeOut);
  private isRunning: boolean = false;
  private isConfirmationDialogOpen: boolean = false;
  private shouldShowDialogOnReturn: boolean = false;
  private hasLoggedOut: boolean = false; // Track if logout has occurred
  private lastDialogShownTime: number = 0; // Prevent rapid re-showing of dialog
  private internalTimerID;
  confirmDialogRef: MatDialogRef<IdleDialogComponent>;
  private crossTabSessionService: CrossTabSessionService;
  
  constructor(private dialog: MatDialog, private _alnIdentityAuthService: ALNIdentityAuthService, private ngZone: NgZone, 
    private injector: Injector) {
    this.watchForActivity();
    this.watchForVisibilityChange();
    // Inject CrossTabSessionService through injector to avoid circular dependency
    this.crossTabSessionService = this.injector.get(CrossTabSessionService);
  }

  private watchForActivity(): void {
    window.addEventListener('mousemove', this.resetTimer.bind(this));
    window.addEventListener('keypress', this.resetTimer.bind(this));
  }

  private watchForVisibilityChange(): void {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // User returned to tab
        if (this.hasLoggedOut) {
          // User was already logged out - they should already be redirected
          // This is just a safety net in case redirect didn't work
          window.location.href = '/login';
          return;
        }
        
        if (this.shouldShowDialogOnReturn) {
          this.shouldShowDialogOnReturn = false;
          this.showTimeoutConfirmation();
        }
      }
    });
  }

  resetTimer(): void {
    // Don't reset timer while confirmation dialog is open to prevent bypassing timeout
    // Note: Continue button bypasses this check by directly calling setLastActiveTime()
    if (this.isConfirmationDialogOpen) {
      return;
    }
    this.setLastActiveTime(Date.now());
  }

  getLastActiveTime(): number {
    const lastActiveDateTime = localStorage.getItem('lastActiveDT_' + window.location.host);
    if(lastActiveDateTime){
      return Number(lastActiveDateTime);
    }
    else{
      return 0;
    }
  }

  setLastActiveTime(lastActiveDateTime: number): void {
    localStorage.setItem("lastActiveDT_" + window.location.host, lastActiveDateTime.toString());
  }

  private showTimeoutConfirmation(): void {
    // Prevent showing dialog too frequently (within 5 seconds of last dialog)
    const currentTime = Date.now();
    if (currentTime - this.lastDialogShownTime < 5000) {
      return;
    }
    
    this.isConfirmationDialogOpen = true;
    this.lastDialogShownTime = currentTime;
    
    this.confirmDialogRef = this.dialog.open(IdleDialogComponent, { disableClose: true, data: { isAlert: true } });
    this.confirmDialogRef.componentInstance.title = 'Session Timeout';
    this.confirmDialogRef.componentInstance.timeoutWarning = this.Message_TimeOut;
    
    // Set up immediate callback for Continue button to reset timer
    // Directly sets last active time to bypass the dialog-open check in resetTimer()
    this.confirmDialogRef.componentInstance.onContinueCallback = () => {
      this.setLastActiveTime(Date.now());
    };
    
    this.confirmDialogRef.afterClosed().subscribe(result => {
      this.isConfirmationDialogOpen = false;
      
      if (result === 'stay') {
        // User clicked Continue - timer was already reset by callback
      } else {
        // Either user clicked 'logout' button or dialog countdown reached 0
        // Both cases should trigger logout via auth service (which handles redirect)
        this.logoutViaAuthService('IdleTimeout');
      }
    });
  }


  isTimedout(): void {
    const userLoggedIn = localStorage.getItem("userLoggedIn");
    if (userLoggedIn === "false" && this.hasLoggedOut)
    {
      console.log('IdleTimeoutService: User logged out flag detected with previous logout, forcing redirect');
      this.logoutWithForcedRedirect('browserTab');
      return;
    }
    const lastActiveTime = this.getLastActiveTime();
    const currentTime = Date.now();
    const timeSinceLastActive = (currentTime - lastActiveTime) / 1000;
    

    
    // Check if full timeout period has elapsed - logout immediately regardless of tab visibility
    if (timeSinceLastActive >= this.MAX_IDLE_TIME) {
      
      // Broadcast session timeout to other tabs before logging out
      if (this.crossTabSessionService) {
        try {
          this.crossTabSessionService.broadcastSessionTimeout();
        } catch (error) {
          console.error('IdleTimeoutService: Error broadcasting session timeout', error);
        }
      }
      
      this.logoutWithForcedRedirect(`IdleTimeout&ct=${currentTime}&lat=${lastActiveTime}`);
      return;
    }
    
    // Show timeout confirmation dialog when approaching the idle limit
    if ((timeSinceLastActive + this.Message_TimeOut) >= this.MAX_IDLE_TIME && !this.isConfirmationDialogOpen) {
      if (document.hidden) {
        // Tab is not visible - mark that dialog should show when user returns
        this.shouldShowDialogOnReturn = true;
      } else {
        // Tab is visible - show dialog immediately
        this.showTimeoutConfirmation();
      }
    }
  }

  stop(): void {
    this.isRunning = false;
    this.isConfirmationDialogOpen = false;
    this.shouldShowDialogOnReturn = false;
    // Note: hasLoggedOut is not reset here because stop() is called from logout methods
    // and we need to preserve this flag to prevent re-login attempts
    this.lastDialogShownTime = 0;
    clearInterval(this.internalTimerID);
    
    if (this.confirmDialogRef) {
      this.confirmDialogRef.close();
      this.confirmDialogRef = null;
    }
  }

  start(): void {
    if(!this.isRunning){
      this.hasLoggedOut = false;
      this.isRunning = true;
      
      const lastActiveTime = this.getLastActiveTime();
      if (lastActiveTime === 0 || isNaN(lastActiveTime)) {
        this.resetTimer();
      }
      
      this.ngZone.runOutsideAngular(() => {
        this.internalTimerID = setInterval(() => {
          this.isTimedout();
        }, 1000);
      });
    }
  }

  // Logout via auth service and ensure redirect to login page
  // Used when timeout expires completely or user manually logs out
  logoutWithForcedRedirect(callOrigin: string): void {
    this.hasLoggedOut = true;
    this.stop();
    
    // Call auth service logout which may handle its own redirect
    if (this._alnIdentityAuthService && typeof this._alnIdentityAuthService.logout === 'function') {
      this._alnIdentityAuthService.logout(callOrigin);
    } else {
      console.info(`Logging off via injector`);
      let alnIdentityAuthService = this.injector.get(ALNIdentityAuthService);
      alnIdentityAuthService.logout(callOrigin);
    }
    
    // Fallback: ensure redirect to login page happens if auth service doesn't redirect
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }

  // Logout via auth service only (auth service handles redirect)
  // Used when user clicks logout button in dialog
  logoutViaAuthService(callOrigin: string): void {
    this.hasLoggedOut = true;
    this.stop();
    
    // Call auth service logout which will handle the redirect
    if (this._alnIdentityAuthService && typeof this._alnIdentityAuthService.logout === 'function') {
      this._alnIdentityAuthService.logout(callOrigin);
    } else {
      console.info(`Logging off via injector`);
      let alnIdentityAuthService = this.injector.get(ALNIdentityAuthService);
      alnIdentityAuthService.logout(callOrigin);
    }
  }
}
